import { useEffect, useState } from "react";
import { Loader2, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", email: "", password: "", role: "tehnician" };

const AddUserDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", f);
      toast.success(`Cont creat pentru ${f.name}`);
      setF(EMPTY); setOpen(false); onCreated();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-user-button"><UserPlus className="h-4 w-4" /> Adaugă coleg</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Cont nou</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-user-form">
          <div className="space-y-1.5"><Label>Nume</Label><Input required value={f.name} onChange={set("name")} data-testid="new-user-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input required type="email" value={f.email} onChange={set("email")} data-testid="new-user-email-input" /></div>
          <div className="space-y-1.5"><Label>Parolă (min. 6 caractere)</Label><Input required minLength={6} value={f.password} onChange={set("password")} data-testid="new-user-password-input" /></div>
          <div className="space-y-1.5">
            <Label>Rol</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tehnician" data-testid="role-option-tehnician">Tehnician</SelectItem>
                <SelectItem value="admin" data-testid="role-option-admin">Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={saving} className="w-full" data-testid="new-user-submit-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Creează cont
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Ștergi contul lui ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Cont șters"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="team-management-view">
      <PageHeader title="Echipă" subtitle="Colegii care pot adăuga și rezolva tichete."><AddUserDialog onCreated={load} /></PageHeader>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="team-list">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-4 rounded-xl border bg-card p-5" data-testid={`team-member-${u.id}`}>
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-primary/15 font-display text-lg font-bold text-primary">{u.name[0]?.toUpperCase()}</div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{u.name}</div>
              <div className="truncate text-xs text-muted-foreground">{u.email}</div>
              <div className="mt-1 flex gap-2 text-xs">
                <span className="rounded-full border px-2 py-0.5 capitalize">{u.role}</span>
                <span className="text-muted-foreground" data-testid={`team-open-${u.id}`}>{u.open_tickets} tichete active</span>
              </div>
            </div>
            {u.id !== user.id && (
              <button onClick={() => remove(u)} className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive" data-testid={`delete-user-${u.id}`} aria-label="Șterge"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
